import React from 'react';
import { Container, Row, Col, Form, BodyText } from 'shared-portal-component';
import { z } from 'zod';

import type { UpdatePasswordPayload } from '@/types/passwordUpdate/PasswordUpdate';

const MIN_PASSWORD_LENGTH = 12;

const schema = z
  .object({
    oldPassword: z.string().min(1, 'Current password is required'),
    newPassword: z
      .string()
      .min(MIN_PASSWORD_LENGTH, 'Password must be at least 12 characters long')
      .regex(/[A-Z]/, 'Must contain at least 1 uppercase letter')
      .regex(/[a-z]/, 'Must contain at least 1 lowercase letter')
      .regex(/\d/, 'Must contain at least 1 number')
      .regex(/[^A-Za-z0-9]/, 'Must contain at least 1 special character'),
    confirmNewPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: 'Passwords do not match',
    path: ['confirmNewPassword'],
  })
  .refine((data) => data.oldPassword !== data.newPassword, {
    message: 'New password must differ from current password',
    path: ['newPassword'],
  });

type FormValues = z.infer<typeof schema>;

const fields = [
  {
    name: 'oldPassword',
    label: 'Current Password',
    placeholder: 'Enter your current password',
    required: true,
    variant: 'outline' as const,
    type: 'password',
  },
  {
    name: 'newPassword',
    label: 'New Password',
    placeholder: 'Enter a new password',
    required: true,
    variant: 'outline' as const,
    type: 'password',
  },
  {
    name: 'confirmNewPassword',
    label: 'Confirm New Password',
    placeholder: 'Re-enter your new password',
    required: true,
    variant: 'outline' as const,
    type: 'password',
  },
];

const passwordRequirements = [
  'At least 12 characters long',
  'At least 1 uppercase letter',
  'At least 1 lowercase letter',
  'At least 1 number',
  'At least 1 special character',
];

interface UpdatePasswordFormProps {
  onSubmit: (values: UpdatePasswordPayload) => void;
  error?: string;
}

export default function UpdatePasswordForm({ onSubmit, error }: UpdatePasswordFormProps) {
  return (
    <Container fluid>
      <Row spacing="md">
        <Col size={12}>
          <BodyText as="p" className="mb-4">
            New passwords must meet the following requirements:
          </BodyText>
          <ul className="mb-4 list-disc pl-6">
            {passwordRequirements.map((requirement) => (
              <li key={requirement}>
                <BodyText as="span">{requirement}</BodyText>
              </li>
            ))}
          </ul>

          {typeof error === 'string' && error.length > 0 && (
            <BodyText as="p" className="text-destructive mb-4">
              {error}
            </BodyText>
          )}
        </Col>
      </Row>

      <Form
        schema={schema}
        fields={fields}
        onSubmit={(values: FormValues) => {
          onSubmit({
            oldPassword: values.oldPassword,
            newPassword: values.newPassword,
          });
        }}
      />
    </Container>
  );
}
